import React, { useCallback, useEffect, useState } from "react";
import { UserPlus, KeyRound, Trash2, RefreshCw, ShieldAlert } from "lucide-react";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

// Rollen wie im Backend (services/collections + requireRole). Reihenfolge so,
// dass der haeufigste Fall – ein Stationsbetreuer – vorbelegt ist.
const ROLLEN = [
  { wert: "betreuer", label: "Betreuer – Stationen & Ergebnisse eintragen" },
  { wert: "admin", label: "Admin – Vollzugriff" },
  { wert: "quiz", label: "Quiz-Tablet – nur das Quiz, keine Ergebnisse" },
];

// "gast" ist nicht mehr anlegbar: Die Rolle hatte nie eine eigene Ansicht – ein
// Gast landete nach der Anmeldung auf "Keine Berechtigung". Bestehende
// Gast-Zugaenge werden noch angezeigt (und lassen sich loeschen).
function rolleLabel(wert) {
  if (wert === "gast") return "Gast (veraltet – ohne eigene Ansicht)";
  return ROLLEN.find((r) => r.wert === wert)?.label || wert || "—";
}

export default function Nutzerverwaltung() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("token");
  const meinName = localStorage.getItem("username");

  const [users, setUsers] = useState([]);
  const [laden, setLaden] = useState(true);
  const [ladeFehler, setLadeFehler] = useState(null);

  const [form, setForm] = useState({ username: "", role: "betreuer", password: "" });
  const [anlegen, setAnlegen] = useState(false);
  const [hinweis, setHinweis] = useState(null); // { art: "ok" | "fehler", text }

  const authHeaders = useCallback(
    (json = false) => ({
      ...(json ? { "Content-Type": "application/json" } : {}),
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const ladeUsers = useCallback(async () => {
    setLaden(true);
    setLadeFehler(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/users`, { headers: authHeaders() }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Nutzer konnten nicht geladen werden.");
      setUsers(data.users || []);
    } catch (err) {
      setLadeFehler(err.message || String(err));
    } finally {
      setLaden(false);
    }
  }, [BACKEND_URL, authHeaders]);

  useEffect(() => {
    ladeUsers();
  }, [ladeUsers]);

  const neuAnlegen = async (e) => {
    e.preventDefault();
    setHinweis(null);
    setAnlegen(true);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/users`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          username: form.username.trim(),
          role: form.role,
          password: form.password,
        }),
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Anlegen fehlgeschlagen.");
      setHinweis({ art: "ok", text: `Nutzer „${form.username.trim()}“ (${form.role}) angelegt.` });
      setForm({ username: "", role: "betreuer", password: "" });
      ladeUsers();
    } catch (err) {
      setHinweis({ art: "fehler", text: err.message || String(err) });
    } finally {
      setAnlegen(false);
    }
  };

  const passwortAendern = async (username) => {
    const neu = window.prompt(`Neues Passwort für „${username}“:`);
    if (neu === null) return; // Abbrechen im Dialog
    if (neu.length < 1) {
      setHinweis({ art: "fehler", text: "Passwort darf nicht leer sein." });
      return;
    }
    setHinweis(null);
    try {
      const res = mitSitzungspruefung(await fetch(
        `${BACKEND_URL}/admin/users/${encodeURIComponent(username)}/password`,
        { method: "POST", headers: authHeaders(true), body: JSON.stringify({ password: neu }) }
      ));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Passwort konnte nicht geändert werden.");
      setHinweis({ art: "ok", text: `Passwort für „${username}“ geändert.` });
    } catch (err) {
      setHinweis({ art: "fehler", text: err.message || String(err) });
    }
  };

  const loeschen = async (username) => {
    if (!window.confirm(`Nutzer „${username}“ wirklich löschen?`)) return;
    setHinweis(null);
    try {
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/admin/users/${encodeURIComponent(username)}`, {
        method: "DELETE",
        headers: authHeaders(),
      }));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Löschen fehlgeschlagen.");
      setHinweis({ art: "ok", text: `Nutzer „${username}“ gelöscht.` });
      ladeUsers();
    } catch (err) {
      setHinweis({ art: "fehler", text: err.message || String(err) });
    }
  };

  const adminPwSchwach = form.role === "admin" && form.password.length > 0 && form.password.length < 8;

  return (
    <div className="p-6 bg-secondary-950 text-white min-h-screen">
      <h1 className="text-2xl font-bold mb-2">Nutzerverwaltung</h1>
      <p className="text-sm text-gray-400 mb-6 max-w-2xl">
        Passwörter werden verschlüsselt gespeichert und können nicht wieder angezeigt
        werden – bei Bedarf einfach ein neues setzen. Betreuer-Passwörter dürfen ruhig
        einfach sein; halte den Admin-Zugang davon getrennt.
      </p>

      {hinweis && (
        <p className={`text-sm mb-4 ${hinweis.art === "fehler" ? "text-red-400" : "text-green-400"}`}>
          {hinweis.art === "fehler" ? "❌ " : "✅ "}
          {hinweis.text}
        </p>
      )}

      {/* === Neuen Nutzer anlegen === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <UserPlus className="text-primary-400" />
          <h2 className="font-semibold text-lg">Neuen Nutzer anlegen</h2>
        </div>

        <form onSubmit={neuAnlegen} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Benutzername</span>
            <input
              type="text"
              required
              minLength={2}
              maxLength={50}
              autoComplete="off"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-primary-500"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Rolle</span>
            <select
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-primary-500"
            >
              {ROLLEN.map((r) => (
                <option key={r.wert} value={r.wert}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-400">Passwort</span>
            <input
              type="text"
              required
              maxLength={200}
              autoComplete="off"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className="px-3 py-2 rounded bg-secondary-950 border border-secondary-700 text-white focus:outline-none focus:border-primary-500"
            />
          </label>

          <div className="sm:col-span-3 flex flex-col gap-2">
            {adminPwSchwach && (
              <p className="text-amber-400 text-xs flex items-center gap-1">
                <ShieldAlert size={14} />
                Für einen Admin-Zugang besser ein längeres Passwort (mind. 8 Zeichen) wählen.
              </p>
            )}
            <button
              type="submit"
              disabled={anlegen}
              className="w-full sm:w-auto px-4 py-2 rounded bg-primary-500 hover:bg-primary-600 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <UserPlus size={18} />
              {anlegen ? "wird angelegt…" : "Nutzer anlegen"}
            </button>
          </div>
        </form>
      </div>

      {/* === Bestehende Nutzer === */}
      <div className="bg-secondary-900 border border-secondary-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Bestehende Nutzer</h2>
          <button
            onClick={ladeUsers}
            disabled={laden}
            className="px-3 py-1.5 rounded bg-secondary-800 hover:bg-secondary-700 text-sm flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCw size={14} className={laden ? "animate-spin" : ""} />
            Neu laden
          </button>
        </div>

        {ladeFehler && <p className="text-red-400 text-sm mb-3">❌ {ladeFehler}</p>}

        {laden ? (
          <p className="text-gray-400 text-sm">Lade Nutzer…</p>
        ) : users.length === 0 ? (
          <p className="text-gray-400 text-sm">Noch keine Nutzer angelegt.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 border-b border-secondary-800">
                  <th className="py-2 pr-4 font-medium">Benutzername</th>
                  <th className="py-2 pr-4 font-medium">Rolle</th>
                  <th className="py-2 pr-4 font-medium">Letzter Login</th>
                  <th className="py-2 font-medium text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const binIch = u.username === meinName;
                  return (
                    <tr key={u.username} className="border-b border-secondary-800/60">
                      <td className="py-2 pr-4">
                        {u.username}
                        {binIch && <span className="text-xs text-gray-500"> (du)</span>}
                        {u.needs_migration && (
                          <span className="ml-2 text-xs text-amber-400">
                            (Klartext – wird beim nächsten Login umgestellt)
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4">{rolleLabel(u.role)}</td>
                      <td className="py-2 pr-4 text-gray-400">
                        {u.last_login ? new Date(u.last_login).toLocaleString("de-DE") : "nie"}
                      </td>
                      <td className="py-2">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => passwortAendern(u.username)}
                            className="px-2.5 py-1.5 rounded bg-secondary-800 hover:bg-secondary-700 flex items-center gap-1"
                            title="Passwort ändern"
                          >
                            <KeyRound size={14} /> Passwort
                          </button>
                          <button
                            onClick={() => loeschen(u.username)}
                            disabled={binIch}
                            className="px-2.5 py-1.5 rounded bg-red-700 hover:bg-red-600 text-white flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={binIch ? "Eigenen Zugang nicht löschbar" : "Nutzer löschen"}
                          >
                            <Trash2 size={14} /> Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
